/*
 * @ORGANIZATION DETAIL PAGE
 * */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  Signal,
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
  Routes,
} from '@angular/router';

import {
  DEFAULT_CONTACT_ROUTES,
  CONTACT_DETAIL_PROVIDER,
  PROFILE_PAGE_PROVIDER,
  ProfileService,
  externalProfileOnlyGuard,
  ContactService,
} from '@data-access/profiles';
import {
  BannerType,
  WcgBannerComponent,
  WcgTableService,
} from '@wcg/frontend-framework';
import { MatMenuModule } from '@angular/material/menu';

import {
  contactResolver,
  profileResolver,
} from '@data-access/profiles/resolver/contact.resolver';
import { StudyListFacadeService } from '@data-access/shared/facades/study-list.facade.service';
import { studyListResolver } from '@data-access/shared/resolvers/study.resolver';
import { siteListResolver } from '@data-access/shared/resolvers/site.resolver';
import { PermissionsService } from '@shared/services/permissions.service';

import {
  permissionProviders,
  ProfileHeaderWrapperComponent,
  SITE_LIST_PROVIDER,
  StickyContentOffsetClassBasedDirective,
  STUDY_LIST_PROVIDER,
} from '@data-access/shared';
import { PEOPLE_ADMIN_PERMISSIONS } from '@data-access/permissions';
import { OktaAuthGuard } from '../../guards/okta-auth';
import { canMatchInternalUser } from '../../guards/permissions';
import { profilePageTokenizer } from '@data-access/profiles/store/token/profile.token';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { IUser } from '@shared/user.interface';
import { OrganizationService } from '@data-access/organizations';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatMenuModule,
    StickyContentOffsetClassBasedDirective,
    WcgBannerComponent,
    ProfileHeaderWrapperComponent,
  ],
  selector: 'wcg-contact-detail-page',
  styleUrls: ['../../shared/styles/page-nav.css'],
  template: `
    @defer (when !$isLoading()) {
      <!--      @let data = $data() | keyvalue;-->
      @if (error) {
        <wcg-banner [bannerType]="bannerType.ALERT"
          >Unable to load contact details. Please try again.
        </wcg-banner>
      } @else {
        <wcg-profile-header-wrapper
          [data]="$profile().info"
          [viewedUserId]="$profile().user_id"
          [skipImpersonation]="$skipImpersonation()"
          [isInternal]="!!$isInternal()!"
          [currentUser]="$currentUser()"
        />
        <div
          class="flex w-full flex-col page-content-sticky !bg-blue-grey-04"
          wcgStickyContentOffsetClassBased
        >
          <div class="organization-page-headers page-nav flex flex-row gap-4">
            @let routes = $defaultRoutes();
            @for (route of routes; track route.path) {
              <a
                [routerLink]="route.path"
                routerLinkActive="is-active"
                [attr.data-testid]="route.name"
              >
                {{ route.name }}
              </a>
            }
          </div>
        </div>
        <div class="w-full mt-7 relative">
          <router-outlet />
        </div>
      }
    } @placeholder {
      <div class="flex justify-center items-center p-8">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
        ></div>
        <span class="ml-3 text-gray-600">Loading contact details...</span>
      </div>
    }
  `,
})
export class ContactDetailPageComponent implements OnDestroy {
  private readonly _profileTokenizer = profilePageTokenizer();
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _permissionsService = inject(PermissionsService);

  defaultRoutes = DEFAULT_CONTACT_ROUTES;

  readonly $skipImpersonation: Signal<boolean> = toSignal(
    this._route.data.pipe(map((data) => data['skipImpersonation'] ?? false)),
  );

  readonly $defaultRoutes = computed(() => {
    const routes = [...this.defaultRoutes];
    if (this.$profile()?.info.role.isInternal) {
      return routes.filter((route) => route.type !== 'external');
    }
    return routes;
  });
  bannerType = BannerType;

  readonly $isLoading = computed(() => {
    const loading = this._profileTokenizer.$loading();
    return loading?.isLoading ?? false;
  });

  error = false;

  readonly $profile = computed(() => this._profileTokenizer.$profileData());

  readonly $isInternal = toSignal(this._permissionsService.isInternal$);

  readonly $currentUser = toSignal(
    this._permissionsService.currentUser$.pipe(
      filter((user): user is IUser => !!user),
    ),
  );

  /*
   * Lifecycle hooks
   * */
  ngOnDestroy(): void {
    this._profileTokenizer.clearProfile();
  }
  /* End lifecycle hooks * */

  goToTab(path: string): void {
    this._router.navigate([path], { relativeTo: this._route });
  }
}

// NOTE: TO AVOID CONFUSION THIS IS THE PEOPLE'S PAGE
export const CONTACT_DETAIL_ROUTES: Routes = [
  {
    children: [
      {
        path: '',
        pathMatch: 'prefix',
        redirectTo: 'overview',
      },
      {
        canMatch: [OktaAuthGuard, canMatchInternalUser],
        loadComponent: () =>
          import('../features/contact-overview').then(
            (m) => m.ProfileOverviewComponent,
          ),
        path: 'overview',
      },
      {
        data: {
          type: 'contact',
        },
        loadComponent: () =>
          import('../../iris-sites/pages').then((m) => m.SitePageComponent),
        path: 'sites',
        providers: [SITE_LIST_PROVIDER, OrganizationService, WcgTableService],
        resolve: {
          resolvedData: siteListResolver,
        },
        canMatch: [externalProfileOnlyGuard],
      },
      {
        data: {
          type: 'contact',
        },
        loadComponent: () =>
          import('../../study/pages').then((m) => m.StudyPageComponent),
        path: 'studies',
        providers: [
          STUDY_LIST_PROVIDER,
          StudyListFacadeService,
          OrganizationService,
          WcgTableService,
        ],
        resolve: {
          resolvedData: studyListResolver,
        },
        canMatch: [externalProfileOnlyGuard],
      },
    ],
    component: ContactDetailPageComponent,
    path: ':id',
    providers: [
      PROFILE_PAGE_PROVIDER,
      CONTACT_DETAIL_PROVIDER,
      ProfileService,
      ContactService,
      ...permissionProviders({ ...PEOPLE_ADMIN_PERMISSIONS() }),
    ],
    data: {
      skipImpersonation: false,
    },
    resolve: {
      resolvedData: contactResolver,
      resolveProfileData: profileResolver,
    },
  },
];
